import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useCreateSender, useSenderAddressDefaults } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Mail, Save, TriangleAlert } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { createSenderPayload, EMPTY_SENDER_FORM, senderCreateReady, type SenderFormData } from './sender-form'
import { SenderFormFields } from './sender-form-fields'

export function AdminSenderNew() {
  const navigate = useNavigate()
  const { perform: createSender, loading: saving } = useCreateSender()
  const { data: addressDefaults, loading: loadingAddress } = useSenderAddressDefaults()
  const [search] = useSearchParams()
  const [formData, setFormData] = useState<SenderFormData>(() => ({
    ...EMPTY_SENDER_FORM,
    method: search.get('method') === 'SMS' ? 'SMS' : 'EMAIL',
  }))
  const [error, setError] = useState<string | null>(null)

  const addressMissing = formData.method === 'EMAIL' && addressDefaults?.defaults === null

  const handleFieldChange = useCallback((field: keyof SenderFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      try {
        const created = await createSender(createSenderPayload(formData))
        navigate(`/admin/senders/${created.id}`, { replace: true })
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to create sender'))
      }
    },
    [formData, createSender, navigate],
  )

  const canSubmit = senderCreateReady(formData, saving, loadingAddress, Boolean(addressMissing))

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/admin/senders">Back to Senders</BackLink>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New Sender</CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          {addressMissing ? (
            <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div className="text-sm">
                <p className="font-medium text-amber-500">Configure sender address defaults first</p>
                <p className="mt-1 text-muted-foreground">
                  SendGrid requires a physical address on every sender. Set this once on the{' '}
                  <Link to="/admin/integrations" className="underline">
                    Integrations
                  </Link>{' '}
                  page.
                </p>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <SenderFormFields
              data={formData}
              onChange={handleFieldChange}
              disabled={saving}
              fromEmailHelper="A verification email will be sent to this address. The sender can't be used until that link is clicked."
            />

            {formData.method === 'EMAIL' ? (
              <div className="mt-4 flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  The org-default address ({addressDefaults?.defaults?.city ?? '—'},{' '}
                  {addressDefaults?.defaults?.country ?? '—'}) will be attached to this sender for CAN-SPAM compliance.
                </div>
              </div>
            ) : null}

            {error ? (
              <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={!canSubmit}>
                <Save className="mr-1 h-4 w-4" />
                {saving ? 'Creating...' : 'Create Sender'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Page>
  )
}
